/**
 * O editor de uma operação SQL: escrever, testar, publicar e aprovar.
 *
 * - O SQL se escreve com o mapa do banco ao lado; clicar num nome o insere.
 * - Os parâmetros saem do SQL (o marcador da família); a pessoa dá o nome, o
 *   tipo e um valor de teste.
 * - "Testar" roda a versão SALVA: a escrita, numa transação desfeita. Por isso
 *   o teste fica travado enquanto há alteração não salva.
 * - Salvar cria uma versão nova. A leitura sai publicada; a escrita fica
 *   esperando outra pessoa da Cliqfy aprovar, e o autor não aprova a própria.
 * - "Executar ao vivo" grava de verdade no banco do cliente: pede confirmação,
 *   e a escrita só roda aprovada.
 */

import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErroDaApi } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import {
  useApi,
  useConexoesDeDados,
  useHistoricoDaOperacaoDeDados,
  useMapaDoBanco,
  useOperacaoDeDados,
} from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useSessao } from '@/shared/sessao/contexto';
import {
  Botao,
  CabecalhoDeTela,
  Campo,
  CampoSelecao,
  Dialogo,
  Icone,
  Segmentado,
  Selo,
  idDoPainel,
} from '@/shared/ui';
import { Estado, Vazio } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  FINALIDADES,
  LIMITES,
  TIPOS_DA_COLUNA,
  TIPOS_DO_PARAMETRO,
  ajustarParametros,
  celula,
  colunasDoResultado,
  corpoParaSalvar,
  esperaAprovacao,
  travar,
  formularioDe,
  formularioVazio,
  marcadorDe,
  mudou,
  oQueAconteceu,
  parametrosDoSql,
  porQueNaoAprova,
  problemasDoFormulario,
  quemFez,
  situacaoDe,
  tipoProvavel,
  valoresDeTeste,
  type ColunaDeSaida,
  type Familia,
  type Finalidade,
  type Formulario,
  type Operacao,
  type Problemas,
  type TipoDoParametro,
} from '../lib/operacoes';
import { EditorSql, type EditorSqlRef, type TabelaDoMapa } from './editor-sql';
import './editor-da-operacao.css';

const SERVICO_FORA =
  'O serviço das automações não respondeu agora. Nada foi alterado; tente de novo daqui a pouco.';
const BANCO_FORA =
  'O banco do cliente não respondeu. Teste a conexão em Integrações para ver em que etapa para.';

type Aba = 'parametros' | 'colunas' | 'limites' | 'resultado';
type Resultado = {
  tipo: 'leitura' | 'escrita';
  linhas: Record<string, unknown>[];
  total: number;
  cortada: boolean;
};

export function EditorDaOperacao({
  id,
  aoVoltar,
  aoCriada,
  aoIrParaIntegracoes,
}: {
  id: string | null;
  aoVoltar: () => void;
  aoCriada: (id: string) => void;
  aoIrParaIntegracoes: () => void;
}): ReactNode {
  const conexoes = useConexoesDeDados();
  if (id === null) {
    return (
      <Estado consulta={conexoes} forma="ficha">
        {(c) =>
          c.itens.length === 0 ? (
            <Vazio
              titulo="Nenhuma conexão ainda"
              porque="A operação lê de uma conexão ao banco do cliente. Cadastre uma em Integrações primeiro."
              acao={{ rotulo: 'Cadastrar uma conexão', aoClicar: aoIrParaIntegracoes }}
            />
          ) : (
            <Edicao salva={null} conexoes={c.itens} aoVoltar={aoVoltar} aoCriada={aoCriada} />
          )
        }
      </Estado>
    );
  }
  return <EdicaoDaSalva id={id} aoVoltar={aoVoltar} aoCriada={aoCriada} />;
}

function EdicaoDaSalva({
  id,
  aoVoltar,
  aoCriada,
}: {
  id: string;
  aoVoltar: () => void;
  aoCriada: (id: string) => void;
}): ReactNode {
  const operacao = useOperacaoDeDados(id);
  const conexoes = useConexoesDeDados();
  return (
    <Estado consulta={operacao} forma="ficha">
      {(o) => (
        <Estado consulta={conexoes} forma="ficha">
          {(c) => (
            <Edicao
              key={`${o.id}:${o.versao}`}
              salva={o}
              conexoes={c.itens}
              aoVoltar={aoVoltar}
              aoCriada={aoCriada}
            />
          )}
        </Estado>
      )}
    </Estado>
  );
}

type Conexao = {
  id: string;
  nome: string;
  familia: Familia;
  situacao: 'ok' | 'falhou' | 'nao_testada';
};

const SITUACAO_DA_CONEXAO: Record<Conexao['situacao'], string> = {
  ok: '',
  falhou: ' · falhou no último teste',
  nao_testada: ' · nunca testada',
};

/** Os campos que o serviço aponta num 422, levados ao campo da tela. */
function problemasDoServidor(erro: unknown): Problemas {
  if (!(erro instanceof ErroDaApi)) return {};
  const p: Problemas = {};
  for (const falha of erro.problema.errors ?? []) {
    if (falha.campo === 'sql') p['sql'] = falha.problema;
    if (falha.campo === 'colunas') p['colunas'] = falha.problema;
    if (falha.campo === 'nome') p['nome'] = falha.problema;
  }
  return p;
}

function Edicao({
  salva,
  conexoes,
  aoVoltar,
  aoCriada,
}: {
  salva: Operacao | null;
  conexoes: readonly Conexao[];
  aoVoltar: () => void;
  aoCriada: (id: string) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const { estado } = useSessao();
  const meuId = estado.nome === 'autenticado' ? estado.me.usuario.id : null;
  const papel = estado.nome === 'autenticado' ? estado.me.papel : null;
  // O Super Admin aprova a própria escrita (ADR-0032): o recado diz quem aprova.
  const quemAprova =
    papel === 'super_admin' ? 'você a aprovar, aqui embaixo' : 'outra pessoa da Cliqfy aprovar';
  const editor = useRef<EditorSqlRef>(null);

  const [f, definirF] = useState<Formulario>(() =>
    salva ? formularioDe(salva) : formularioVazio(conexoes[0]?.id ?? '', 'consulta'),
  );
  const [aba, definirAba] = useState<Aba>('parametros');
  const [tentou, definirTentou] = useState(false);
  const [chave, definirChave] = useState(() => crypto.randomUUID());
  const [resultado, definirResultado] = useState<{ ao_vivo: boolean; r: Resultado } | null>(null);
  const [confirmandoExecucao, definirConfirmandoExecucao] = useState(false);
  const [recusando, definirRecusando] = useState(false);
  const [vendoHistorico, definirVendoHistorico] = useState(false);
  const [saindo, definirSaindo] = useState(false);
  const [recado, definirRecado] = useState<string | null>(null);

  const conexao = conexoes.find((c) => c.id === f.conexaoId) ?? null;
  const familia: Familia = conexao?.familia ?? 'postgres';
  const doSql = parametrosDoSql(f.sql, familia);
  const provavel = tipoProvavel(f.sql);
  const alterada = mudou(f, salva);
  // O mesmo mapa do painel ao lado (a mesma chave): alimenta o autocompletar.
  const mapa = useMapaDoBanco(f.conexaoId);

  const salvar = useMutation({
    mutationFn: () =>
      salva
        ? api!.editarOperacaoDeDados({ caminho: { id: salva.id }, corpo: corpoParaSalvar(f) })
        : api!.criarOperacaoDeDados({ corpo: corpoParaSalvar(f), idempotencyKey: chave }),
    onSuccess: (nova) => {
      cache.setQueryData(chaves.operacaoDeDados(nova.id), nova);
      void cache.invalidateQueries({ queryKey: chaves.historicoDaOperacaoDeDados(nova.id) });
      definirChave(crypto.randomUUID());
      if (!salva) aoCriada(nova.id);
    },
    onSettled: () =>
      void cache.invalidateQueries({ queryKey: ['integracoes-de-dados', 'operacoes'] }),
  });

  const problemas = problemasDoFormulario(f);
  const doServidor = salvar.isError ? problemasDoServidor(salvar.error) : {};
  const erro = (campo: string): { erro: string } | Record<string, never> => {
    const texto = (tentou ? problemas[campo] : undefined) ?? doServidor[campo];
    return texto ? { erro: texto } : {};
  };

  const enviar = (): void => {
    definirTentou(true);
    const chavesDosProblemas = Object.keys(problemas);
    if (chavesDosProblemas.length > 0) {
      // O problema pode estar numa aba fechada: ela abre, para a pessoa ver o que falta.
      const abaDoProblema = abaDe(chavesDosProblemas);
      if (abaDoProblema) definirAba(abaDoProblema);
      return;
    }
    if (salvar.isPending || api === null) return;
    definirRecado(null);
    salvar.mutate(undefined, {
      onSuccess: (nova) =>
        definirRecado(
          nova.tipo === 'escrita'
            ? `Versão ${nova.versao} salva. É uma escrita: ela vai ao ar quando ${quemAprova}.`
            : `Versão ${nova.versao} salva e publicada.`,
        ),
    });
  };

  const mudarSql = (sql: string): void =>
    definirF((atual) => ({
      ...atual,
      sql,
      parametros: ajustarParametros(
        atual.parametros,
        parametrosDoSql(sql, conexoes.find((c) => c.id === atual.conexaoId)?.familia ?? 'postgres')
          .quantos,
      ),
    }));

  const mudarConexao = (conexaoId: string): void =>
    definirF((atual) => ({
      ...atual,
      conexaoId,
      parametros: ajustarParametros(
        atual.parametros,
        parametrosDoSql(atual.sql, conexoes.find((c) => c.id === conexaoId)?.familia ?? 'postgres')
          .quantos,
      ),
    }));

  const situacao = salva ? situacaoDe(salva) : null;
  const titulo = f.nome.trim() || (salva ? salva.nome : 'Nova operação');

  return (
    <div className="operacao-sql">
      <CabecalhoDeTela
        titulo={titulo}
        voltar={
          <span className="cab__voltar">
            <Botao
              variante="fantasma"
              icone
              aria-label="Voltar para Operações SQL"
              title="Voltar para Operações SQL"
              onClick={() =>
                alterada && (salva || f.sql.trim()) ? definirSaindo(true) : aoVoltar()
              }
            >
              <Icone nome="voltar" />
            </Botao>
          </span>
        }
        meta={
          <span className="operacao-sql__meta">
            {salva && <Selo tom="neutro">v{salva.versao}</Selo>}
            {situacao && <Selo tom={situacao.tom}>{situacao.rotulo}</Selo>}
            <span className="operacao-sql__salvo" data-alterada={alterada ? 'sim' : undefined}>
              {alterada
                ? salva
                  ? `Alterações não salvas · vão virar a v${salva.versao + 1}`
                  : 'Ainda não salva'
                : 'Tudo salvo'}
            </span>
          </span>
        }
        acoes={
          <>
            {salva && (
              <Botao variante="secundario" onClick={() => definirVendoHistorico(true)}>
                <Icone nome="relogio" />
                Histórico
              </Botao>
            )}
            <Botao
              variante="primario"
              onClick={enviar}
              {...travar(
                salvar.isPending
                  ? 'Salvando…'
                  : !alterada
                    ? 'Nada mudou desde a última versão.'
                    : api === null
                      ? 'Sem sessão: entre de novo.'
                      : null,
              )}
            >
              {salvar.isPending ? 'Salvando…' : salva ? `Salvar v${salva.versao + 1}` : 'Salvar'}
            </Botao>
          </>
        }
      />

      <div className="operacao-sql__recado-linha" data-vazio={recado ? undefined : 'sim'}>
        <p className="operacao-sql__recado" role="status" aria-live="polite">
          {recado ?? ''}
        </p>
      </div>
      {salvar.isError && Object.keys(doServidor).length === 0 && (
        <Faixa tom="bloqueado">
          {recadoDeErro(salvar.error, {
            generica: 'Não foi possível salvar agora. Nada foi alterado; tente de novo.',
            porCodigo: { ENGINE_INDISPONIVEL: SERVICO_FORA },
          })}
        </Faixa>
      )}

      <div className="operacao-sql__grade">
        {conexao && (
          <MapaAoLado
            conexaoId={conexao.id}
            aoInserir={(texto) => editor.current?.inserir(texto)}
          />
        )}

        <section className="operacao-sql__centro" aria-label="O SQL e o que ele recebe e devolve">
          <Campo
            rotulo="Nome da operação"
            placeholder="Ex.: Consultas de amanhã"
            maxLength={120}
            value={f.nome}
            onChange={(e) => definirF({ ...f, nome: e.target.value })}
            {...erro('nome')}
          />
          <div className="operacao-sql__linha">
            <CampoSelecao
              rotulo="Conexão"
              value={f.conexaoId}
              onChange={(e) => mudarConexao(e.target.value)}
              {...erro('conexao')}
            >
              {conexoes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                  {SITUACAO_DA_CONEXAO[c.situacao]}
                </option>
              ))}
            </CampoSelecao>
            <CampoSelecao
              rotulo="Finalidade"
              ajuda={FINALIDADES[f.finalidade].apoio}
              value={f.finalidade}
              onChange={(e) => definirF({ ...f, finalidade: e.target.value as Finalidade })}
            >
              {(Object.keys(FINALIDADES) as Finalidade[]).map((fin) => (
                <option key={fin} value={fin}>
                  {FINALIDADES[fin].rotulo}
                </option>
              ))}
            </CampoSelecao>
          </div>

          <div className="operacao-sql__sql">
            <div className="operacao-sql__sql-topo">
              <span className="operacao-sql__rotulo" id="operacao-sql-rotulo">
                SQL
              </span>
              <span className="operacao-sql__nota">
                Tipo:{' '}
                <strong>
                  {salva && !alterada
                    ? salva.tipo === 'escrita'
                      ? 'escrita'
                      : 'leitura'
                    : provavel === 'escrita'
                      ? 'escrita (provável)'
                      : provavel === 'leitura'
                        ? 'leitura (provável)'
                        : '—'}
                </strong>{' '}
                · vem do SQL, não se escolhe
              </span>
            </div>
            <EditorSql
              ref={editor}
              valor={f.sql}
              aoMudar={mudarSql}
              familia={familia}
              tabelas={mapa.data?.tabelas ?? EM_BRANCO}
              rotulo="SQL da operação"
              descritoPor="operacao-sql-ajuda"
              invalido={Boolean(erro('sql').erro)}
            />
            <p className="operacao-sql__nota" id="operacao-sql-ajuda">
              {erro('sql').erro ? (
                <span className="operacao-sql__erro">{erro('sql').erro}</span>
              ) : (
                <>
                  Um comando só: SELECT, INSERT, UPDATE ou DELETE. Parâmetros como{' '}
                  <code>{marcadorDe(0, familia)}</code>. Ctrl+Espaço sugere tabelas e colunas.
                </>
              )}
            </p>
            {doSql.faltando.length > 0 && (
              <Faixa tom="atencao">
                O SQL pula {doSql.faltando.map((p) => marcadorDe(p, familia)).join(', ')}: os
                parâmetros vão por posição, em sequência. Use todos ou renumere.
              </Faixa>
            )}
            {f.finalidade === 'listagem' && f.parametros.length > 0 && (
              <Faixa tom="atencao">
                A tela de Listas não tem de onde tirar o valor dos parâmetros: a listagem deve rodar
                sem eles. Use valores fixos ou funções de data do banco.
              </Faixa>
            )}
            {provavel === 'escrita' && (!salva || salva.tipo === 'leitura') && (
              <p className="operacao-sql__aviso">
                <Icone nome="alerta" />
                <span>É uma escrita: depois de salva, só vai ao ar quando {quemAprova}.</span>
              </p>
            )}
          </div>

          <Segmentado
            rotulo="Detalhes da operação"
            como="abas"
            aparencia="sublinhado"
            idDasAbas="operacao"
            opcoes={[
              { valor: 'parametros', rotulo: 'Parâmetros', contagem: f.parametros.length },
              { valor: 'colunas', rotulo: 'Colunas de saída', contagem: f.colunas.length },
              { valor: 'limites', rotulo: 'Limites' },
              { valor: 'resultado', rotulo: 'Resultado' },
            ]}
            escolhida={aba}
            aoEscolher={definirAba}
          />
          <div
            role="tabpanel"
            id={idDoPainel('operacao', aba)}
            aria-labelledby={`operacao-${aba}`}
            className="operacao-sql__painel"
          >
            {aba === 'parametros' && (
              <AbaDosParametros
                f={f}
                familia={familia}
                definirF={definirF}
                problemas={tentou ? problemas : {}}
              />
            )}
            {aba === 'colunas' && (
              <AbaDasColunas
                f={f}
                definirF={definirF}
                problemas={tentou ? problemas : {}}
                erroDoServidor={
                  (tentou ? problemas['colunas'] : undefined) ?? doServidor['colunas'] ?? null
                }
                doTeste={resultado && resultado.r.linhas.length > 0 ? resultado.r.linhas : null}
              />
            )}
            {aba === 'limites' && (
              <div className="operacao-sql__linha">
                <Campo
                  rotulo="Máximo de linhas"
                  ajuda="Acima disso, o resultado é cortado."
                  inputMode="numeric"
                  value={f.maximoDeLinhas}
                  onChange={(e) => definirF({ ...f, maximoDeLinhas: e.target.value })}
                  {...erro('maximoDeLinhas')}
                />
                <Campo
                  rotulo="Tempo limite (ms)"
                  ajuda={`Padrão: ${LIMITES.tempo.padrao.toLocaleString('pt-BR')} ms.`}
                  inputMode="numeric"
                  value={f.tempoMaximoMs}
                  onChange={(e) => definirF({ ...f, tempoMaximoMs: e.target.value })}
                  {...erro('tempoMaximoMs')}
                />
              </div>
            )}
            {aba === 'resultado' && <AbaDoResultado resultado={resultado} />}
          </div>
        </section>

        <aside className="operacao-sql__lado" aria-label="Situação e execução">
          <CartaoDaSituacao
            salva={salva}
            meuId={meuId}
            papel={papel}
            alterada={alterada}
            aoRecusar={() => definirRecusando(true)}
            aoAprovada={(o) =>
              definirRecado(`Versão ${o.versao} aprovada e publicada. Ela já pode ser usada.`)
            }
          />
          <CartaoDeRodar
            salva={salva}
            f={f}
            alterada={alterada}
            aoResultado={(r, aoVivo) => {
              definirResultado({ ao_vivo: aoVivo, r });
              definirAba('resultado');
            }}
            aoExecutarAoVivo={() => definirConfirmandoExecucao(true)}
            aoProblemaDeTeste={() => {
              definirTentou(true);
              definirAba('parametros');
            }}
          />
        </aside>
      </div>

      {confirmandoExecucao && salva && (
        <ExecutarAoVivo
          operacao={salva}
          f={f}
          aoFechar={() => definirConfirmandoExecucao(false)}
          aoResultado={(r) => {
            definirConfirmandoExecucao(false);
            definirResultado({ ao_vivo: true, r });
            definirAba('resultado');
          }}
        />
      )}
      {recusando && salva && (
        <RecusarOperacao
          operacao={salva}
          aoFechar={() => definirRecusando(false)}
          aoRecusada={(o) => {
            definirRecusando(false);
            definirRecado(
              `Versão ${o.versao} recusada. Ela não vai ao ar; salvar de novo cria outra para revisão.`,
            );
          }}
        />
      )}
      {vendoHistorico && salva && (
        <HistoricoDaOperacao
          operacao={salva}
          meuId={meuId}
          aoFechar={() => definirVendoHistorico(false)}
        />
      )}
      {saindo && (
        <Dialogo
          titulo="Sair sem salvar?"
          largura="estreita"
          papel="alertdialog"
          aoFechar={() => definirSaindo(false)}
          botoes={
            <>
              <Botao variante="fantasma" onClick={() => definirSaindo(false)}>
                Continuar editando
              </Botao>
              <Botao variante="perigo" onClick={aoVoltar}>
                Sair sem salvar
              </Botao>
            </>
          }
        >
          <p className="operacao-sql__nota">As alterações desta versão se perdem.</p>
        </Dialogo>
      )}
    </div>
  );
}

const EM_BRANCO: readonly TabelaDoMapa[] = [];

/** A aba onde mora o primeiro problema, quando ele não está à vista. */
function abaDe(chaves: readonly string[]): Aba | null {
  if (chaves.some((c) => c === 'nome' || c === 'sql' || c === 'conexao')) return null;
  if (chaves.some((c) => c.startsWith('parametro.') || c.startsWith('teste.'))) return 'parametros';
  if (chaves.some((c) => c.startsWith('coluna'))) return 'colunas';
  if (chaves.some((c) => c === 'maximoDeLinhas' || c === 'tempoMaximoMs')) return 'limites';
  return null;
}

// ── O mapa do banco, ao lado do SQL ──────────────────────────────────────

function MapaAoLado({
  conexaoId,
  aoInserir,
}: {
  conexaoId: string;
  aoInserir: (texto: string) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const mapa = useMapaDoBanco(conexaoId);
  const [busca, definirBusca] = useState('');
  const ler = useMutation({
    mutationFn: () => api!.atualizarMapaDoBanco({ caminho: { id: conexaoId } }),
    onSuccess: (novo) => cache.setQueryData(chaves.mapaDoBanco(conexaoId), novo),
  });

  return (
    <section className="operacao-sql__mapa" aria-label="Mapa do banco">
      <div className="operacao-sql__mapa-topo">
        <h2 className="operacao-sql__titulo-do-lado">Mapa do banco</h2>
        <Botao
          variante="fantasma"
          miudo
          onClick={() => ler.mutate()}
          {...travar(
            ler.isPending ? 'Lendo o banco…' : api === null ? 'Sem sessão: entre de novo.' : null,
          )}
        >
          <Icone nome="repetir" />
          {ler.isPending ? 'Lendo…' : 'Ler de novo'}
        </Botao>
      </div>
      {ler.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(ler.error, {
            generica: 'Não foi possível ler o banco agora. O mapa anterior continua valendo.',
            porCodigo: {
              ENGINE_INDISPONIVEL: SERVICO_FORA,
              BANCO_DO_CLIENTE_INDISPONIVEL: BANCO_FORA,
            },
          })}
        </Faixa>
      )}
      <Estado consulta={mapa} forma="lista" linhas={4}>
        {(dados) => {
          if (dados.versao === 0) {
            return (
              <p className="operacao-sql__nota">
                O banco ainda não foi lido. Clique em “Ler de novo” para ver as tabelas.
              </p>
            );
          }
          const termo = busca.trim().toLowerCase();
          const tabelas = termo
            ? dados.tabelas.filter(
                (t) =>
                  t.nome.toLowerCase().includes(termo) ||
                  t.colunas.some((c) => c.nome.toLowerCase().includes(termo)),
              )
            : dados.tabelas;
          return (
            <>
              <Campo
                type="search"
                icone="buscar"
                rotuloOculto="Filtrar tabelas e colunas"
                placeholder="Filtrar tabelas e colunas"
                value={busca}
                onChange={(e) => definirBusca(e.target.value)}
              />
              <p className="operacao-sql__nota">
                Versão {dados.versao} · clique num nome para inserir no SQL.
              </p>
              <ul className="operacao-sql__tabelas">
                {tabelas.slice(0, 200).map((t) => (
                  <li key={`${t.esquema}.${t.nome}`}>
                    <details
                      className="operacao-sql__tabela"
                      open={termo !== '' && tabelas.length <= 5}
                    >
                      {/* O summary já é o controle de abrir: o inserir fica dentro, não nele. */}
                      <summary className="operacao-sql__nome-da-tabela">
                        {t.esquema}.{t.nome}
                      </summary>
                      <ul className="operacao-sql__colunas-do-mapa">
                        <li>
                          <button
                            type="button"
                            className="operacao-sql__nome-do-mapa"
                            onClick={() => aoInserir(`${t.esquema}.${t.nome}`)}
                          >
                            Inserir a tabela {t.nome}
                          </button>
                        </li>
                        {t.colunas.map((c) => (
                          <li key={c.nome}>
                            <button
                              type="button"
                              className="operacao-sql__nome-do-mapa"
                              onClick={() => aoInserir(c.nome)}
                              aria-label={`Inserir a coluna ${c.nome}`}
                            >
                              {c.nome}
                            </button>
                            <span className="operacao-sql__tipo-do-mapa">{c.tipo}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                ))}
              </ul>
              {tabelas.length > 200 && (
                <p className="operacao-sql__nota">
                  Mostrando 200 de {tabelas.length} tabelas. Filtre para achar a que procura.
                </p>
              )}
            </>
          );
        }}
      </Estado>
    </section>
  );
}

// ── As abas ──────────────────────────────────────────────────────────────

function AbaDosParametros({
  f,
  familia,
  definirF,
  problemas,
}: {
  f: Formulario;
  familia: Familia;
  definirF: (f: Formulario) => void;
  problemas: Problemas;
}): ReactNode {
  if (f.parametros.length === 0) {
    return (
      <p className="operacao-sql__nota">
        O SQL não usa parâmetros. Para receber um valor de fora (uma data, um código), escreva{' '}
        <code>{marcadorDe(0, familia)}</code> onde ele entra.
      </p>
    );
  }
  const mudar = (i: number, mudanca: Partial<Formulario['parametros'][number]>): void =>
    definirF({
      ...f,
      parametros: f.parametros.map((p, j) => (j === i ? { ...p, ...mudanca } : p)),
    });
  return (
    <ul className="operacao-sql__parametros">
      {f.parametros.map((p, i) => (
        <li key={i} className="operacao-sql__parametro">
          <code className="operacao-sql__marcador">{marcadorDe(i, familia)}</code>
          <Campo
            rotulo="Nome"
            spellCheck={false}
            value={p.nome}
            onChange={(e) => mudar(i, { nome: e.target.value })}
            {...(problemas[`parametro.${i}`] ? { erro: problemas[`parametro.${i}`]! } : {})}
          />
          <CampoSelecao
            rotulo="Tipo"
            value={p.tipo}
            onChange={(e) => mudar(i, { tipo: e.target.value as TipoDoParametro })}
          >
            {(Object.keys(TIPOS_DO_PARAMETRO) as TipoDoParametro[]).map((t) => (
              <option key={t} value={t}>
                {TIPOS_DO_PARAMETRO[t]}
              </option>
            ))}
          </CampoSelecao>
          <Campo
            rotulo="Valor de teste"
            placeholder={p.tipo === 'data' ? 'AAAA-MM-DD' : ''}
            value={p.valorDeTeste}
            onChange={(e) => mudar(i, { valorDeTeste: e.target.value })}
            {...(problemas[`teste.${i}`] ? { erro: problemas[`teste.${i}`]! } : {})}
          />
        </li>
      ))}
    </ul>
  );
}

function AbaDasColunas({
  f,
  definirF,
  problemas,
  erroDoServidor,
  doTeste,
}: {
  f: Formulario;
  definirF: (f: Formulario) => void;
  problemas: Problemas;
  erroDoServidor: string | null;
  doTeste: readonly Record<string, unknown>[] | null;
}): ReactNode {
  const mudar = (i: number, mudanca: Partial<ColunaDeSaida>): void =>
    definirF({ ...f, colunas: f.colunas.map((c, j) => (j === i ? { ...c, ...mudanca } : c)) });
  return (
    <div className="operacao-sql__colunas">
      <p className="operacao-sql__nota">
        Só as colunas declaradas voltam para o CRM; as outras são descartadas. Sem nenhuma, a
        operação devolve o que o SELECT trouxer.
      </p>
      {erroDoServidor && <Faixa tom="bloqueado">{erroDoServidor}</Faixa>}
      {f.colunas.length > 0 && (
        <ul className="operacao-sql__lista-de-colunas">
          {f.colunas.map((c, i) => (
            <li key={i} className="operacao-sql__coluna">
              <Campo
                rotulo="Nome"
                spellCheck={false}
                value={c.nome}
                onChange={(e) => mudar(i, { nome: e.target.value })}
                {...(problemas[`coluna.${i}`] ? { erro: problemas[`coluna.${i}`]! } : {})}
              />
              <CampoSelecao
                rotulo="Tipo"
                value={c.tipo}
                onChange={(e) => mudar(i, { tipo: e.target.value as ColunaDeSaida['tipo'] })}
              >
                {(Object.keys(TIPOS_DA_COLUNA) as ColunaDeSaida['tipo'][]).map((t) => (
                  <option key={t} value={t}>
                    {TIPOS_DA_COLUNA[t]}
                  </option>
                ))}
              </CampoSelecao>
              <label className="operacao-sql__marca">
                <input
                  type="checkbox"
                  checked={c.aceita_vazio ?? false}
                  onChange={(e) => mudar(i, { aceita_vazio: e.target.checked })}
                />
                Aceita vazio
              </label>
              <label className="operacao-sql__marca">
                <input
                  type="checkbox"
                  checked={c.opcional ?? false}
                  onChange={(e) => mudar(i, { opcional: e.target.checked })}
                />
                Opcional
              </label>
              <Botao
                variante="fantasma"
                icone
                miudo
                rotulo={`Tirar a coluna ${c.nome || i + 1}`}
                aria-label={`Tirar a coluna ${c.nome || i + 1}`}
                onClick={() => definirF({ ...f, colunas: f.colunas.filter((_, j) => j !== i) })}
              >
                <Icone nome="lixeira" />
              </Botao>
            </li>
          ))}
        </ul>
      )}
      <div className="operacao-sql__acoes-das-colunas">
        <Botao
          variante="secundario"
          miudo
          onClick={() => definirF({ ...f, colunas: [...f.colunas, { nome: '', tipo: 'texto' }] })}
          {...travar(f.colunas.length >= 100 ? 'São no máximo 100 colunas.' : null)}
        >
          <Icone nome="mais" />
          Adicionar coluna
        </Botao>
        <Botao
          variante="secundario"
          miudo
          onClick={() => doTeste && definirF({ ...f, colunas: colunasDoResultado(doTeste) })}
          {...travar(
            doTeste ? null : 'Teste a operação antes: as colunas saem das linhas que voltarem.',
          )}
        >
          Preencher a partir do teste
        </Botao>
      </div>
    </div>
  );
}

function AbaDoResultado({
  resultado,
}: {
  resultado: { ao_vivo: boolean; r: Resultado } | null;
}): ReactNode {
  if (!resultado) {
    return <p className="operacao-sql__nota">Teste a operação para ver o que ela devolve.</p>;
  }
  const { r, ao_vivo } = resultado;
  if (r.tipo === 'escrita') {
    return (
      <p className="operacao-sql__veredito" data-ao-vivo={ao_vivo ? 'sim' : undefined}>
        {ao_vivo
          ? `${r.total === 1 ? '1 linha gravada' : `${r.total} linhas gravadas`} no banco do cliente.`
          : `${r.total === 1 ? '1 linha seria afetada' : `${r.total} linhas seriam afetadas`}. ROLLBACK feito, nada foi gravado.`}
      </p>
    );
  }
  const nomes = r.linhas.length > 0 ? Object.keys(r.linhas[0]!) : [];
  return (
    <div className="operacao-sql__resultado">
      <p className="operacao-sql__nota">
        {ao_vivo ? 'Executada ao vivo' : 'Teste'} ·{' '}
        {r.total === 1 ? '1 linha' : `${r.total} linhas`}
        {r.cortada ? ` · mostrando ${r.linhas.length}, cortado pelo limite` : ''}
      </p>
      {r.linhas.length === 0 ? (
        <p className="operacao-sql__nota">A consulta rodou e não trouxe nenhuma linha.</p>
      ) : (
        <div className="operacao-sql__rolagem">
          <table className="operacao-sql__tabela-do-resultado">
            <thead>
              <tr>
                {nomes.map((n) => (
                  <th key={n} scope="col">
                    {n}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.linhas.slice(0, 100).map((linha, i) => (
                <tr key={i}>
                  {nomes.map((n) => (
                    <td key={n}>{celula(linha[n])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Os cartões do lado ───────────────────────────────────────────────────

function CartaoDaSituacao({
  salva,
  meuId,
  papel,
  alterada,
  aoRecusar,
  aoAprovada,
}: {
  salva: Operacao | null;
  meuId: string | null;
  /** O Super Admin aprova a própria escrita (ADR-0032). */
  papel: string | null;
  alterada: boolean;
  aoRecusar: () => void;
  aoAprovada: (o: Operacao) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const aprovar = useMutation({
    mutationFn: () => api!.aprovarOperacaoDeDados({ caminho: { id: salva!.id } }),
    onSuccess: (o) => {
      cache.setQueryData(chaves.operacaoDeDados(o.id), o);
      aoAprovada(o);
    },
    onSettled: () => {
      void cache.invalidateQueries({ queryKey: ['integracoes-de-dados', 'operacoes'] });
      if (salva)
        void cache.invalidateQueries({ queryKey: chaves.historicoDaOperacaoDeDados(salva.id) });
    },
  });

  if (!salva) {
    return (
      <section className="operacao-sql__cartao" aria-labelledby="operacao-situacao">
        <h2 id="operacao-situacao" className="operacao-sql__titulo-do-lado">
          Situação
        </h2>
        <p className="operacao-sql__nota">
          Ainda não salva. A leitura é publicada ao salvar; a escrita espera outra pessoa da Cliqfy
          aprovar.
        </p>
      </section>
    );
  }

  const situacao = situacaoDe(salva);
  const travada = porQueNaoAprova(salva, meuId, papel);
  return (
    <section className="operacao-sql__cartao" aria-labelledby="operacao-situacao">
      <h2 id="operacao-situacao" className="operacao-sql__titulo-do-lado">
        Situação
      </h2>
      <p>
        <Selo tom={situacao.tom}>{situacao.rotulo}</Selo>
      </p>
      <dl className="operacao-sql__quem">
        <div>
          <dt>Versão {salva.versao} escrita por</dt>
          <dd>{quemFez(salva.autor_id, meuId)}</dd>
        </div>
        {salva.aprovador_id && (
          <div>
            <dt>Aprovada por</dt>
            <dd>{quemFez(salva.aprovador_id, meuId)}</dd>
          </div>
        )}
      </dl>
      {esperaAprovacao(salva) && (
        <>
          <p className="operacao-sql__nota">
            {travada ?? 'Revise o SQL e teste antes de aprovar: aprovada, ela grava de verdade.'}
          </p>
          <div className="operacao-sql__acoes-do-cartao">
            <Botao variante="fantasma" onClick={aoRecusar} {...travar(travada)}>
              Recusar
            </Botao>
            <Botao
              variante="primario"
              onClick={() => aprovar.mutate()}
              {...travar(
                travada ??
                  (alterada
                    ? 'Há alterações não salvas: salve ou descarte antes de aprovar.'
                    : aprovar.isPending
                      ? 'Aprovando…'
                      : null),
              )}
            >
              {aprovar.isPending ? 'Aprovando…' : `Aprovar a v${salva.versao}`}
            </Botao>
          </div>
        </>
      )}
      {aprovar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(aprovar.error, {
            generica: 'Não foi possível aprovar agora. Tente de novo.',
            porCodigo: { ENGINE_INDISPONIVEL: SERVICO_FORA },
          })}
        </Faixa>
      )}
    </section>
  );
}

function CartaoDeRodar({
  salva,
  f,
  alterada,
  aoResultado,
  aoExecutarAoVivo,
  aoProblemaDeTeste,
}: {
  salva: Operacao | null;
  f: Formulario;
  alterada: boolean;
  aoResultado: (r: Resultado, aoVivo: boolean) => void;
  aoExecutarAoVivo: () => void;
  aoProblemaDeTeste: () => void;
}): ReactNode {
  const api = useApi();
  const testar = useMutation({
    mutationFn: (parametros: Record<string, string | number | boolean | null>) =>
      api!.testarOperacaoDeDados({ caminho: { id: salva!.id }, corpo: { parametros } }),
    onSuccess: (r) => aoResultado(r, false),
  });

  const travadoPor = !salva
    ? 'Salve a operação antes de testar.'
    : alterada
      ? 'Salve antes de testar: o teste roda a versão salva.'
      : null;
  const aoVivoTravadoPor =
    travadoPor ??
    (salva && salva.situacao !== 'publicada'
      ? salva.tipo === 'escrita'
        ? 'A escrita só roda ao vivo depois de aprovada.'
        : 'Só a versão publicada roda ao vivo.'
      : null);

  const rodarTeste = (): void => {
    const { valores, problemas } = valoresDeTeste(f.parametros);
    if (Object.keys(problemas).length > 0) {
      aoProblemaDeTeste();
      return;
    }
    testar.mutate(valores);
  };

  return (
    <section className="operacao-sql__cartao" aria-labelledby="operacao-rodar">
      <h2 id="operacao-rodar" className="operacao-sql__titulo-do-lado">
        Rodar
      </h2>
      <p className="operacao-sql__nota">
        O teste usa os valores de teste dos parâmetros. Numa escrita, ele roda numa transação
        desfeita: nada é gravado.
      </p>
      <div className="operacao-sql__acoes-do-cartao">
        <Botao
          variante="secundario"
          onClick={rodarTeste}
          {...travar(travadoPor ?? (testar.isPending ? 'Testando…' : null))}
        >
          <Icone nome="play" />
          {testar.isPending ? 'Testando…' : 'Testar'}
        </Botao>
        <Botao variante="fantasma" onClick={aoExecutarAoVivo} {...travar(aoVivoTravadoPor)}>
          Executar ao vivo
        </Botao>
      </div>
      {testar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(testar.error, {
            generica: 'O teste não rodou. Confira o SQL e os valores e tente de novo.',
            porCodigo: {
              ENGINE_INDISPONIVEL: SERVICO_FORA,
              BANCO_DO_CLIENTE_INDISPONIVEL: BANCO_FORA,
            },
          })}
        </Faixa>
      )}
    </section>
  );
}

// ── Os diálogos ──────────────────────────────────────────────────────────

function ExecutarAoVivo({
  operacao,
  f,
  aoFechar,
  aoResultado,
}: {
  operacao: Operacao;
  f: Formulario;
  aoFechar: () => void;
  aoResultado: (r: Resultado) => void;
}): ReactNode {
  const api = useApi();
  const [chave] = useState(() => crypto.randomUUID());
  const { valores, problemas } = useMemo(() => valoresDeTeste(f.parametros), [f.parametros]);
  const faltam = Object.keys(problemas).length > 0;
  const executar = useMutation({
    mutationFn: () =>
      api!.executarOperacaoDeDados({
        caminho: { id: operacao.id },
        corpo: { parametros: valores },
        idempotencyKey: chave,
      }),
    onSuccess: aoResultado,
  });
  const escrita = operacao.tipo === 'escrita';
  return (
    <Dialogo
      titulo={escrita ? 'Gravar de verdade no banco do cliente?' : 'Executar ao vivo?'}
      largura="estreita"
      papel="alertdialog"
      consequencia={escrita}
      aoFechar={() => {
        if (!executar.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            {...travar(executar.isPending ? 'Executando…' : null)}
          >
            Cancelar
          </Botao>
          <Botao
            variante={escrita ? 'perigo' : 'primario'}
            onClick={() => executar.mutate()}
            {...travar(
              faltam
                ? 'Dê os valores de todos os parâmetros na aba Parâmetros.'
                : executar.isPending
                  ? 'Executando…'
                  : null,
            )}
          >
            {executar.isPending ? 'Executando…' : escrita ? 'Gravar agora' : 'Executar'}
          </Botao>
        </>
      }
    >
      <p className="operacao-sql__nota">
        {escrita
          ? 'A versão aprovada roda com os valores de teste dos parâmetros e grava no banco do cliente. Não há como desfazer daqui.'
          : 'A versão publicada roda com os valores de teste dos parâmetros. Fica registrado na trilha.'}
      </p>
      {executar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(executar.error, {
            generica: 'Não rodou. Nada foi gravado; tente de novo.',
            porCodigo: {
              ENGINE_INDISPONIVEL: SERVICO_FORA,
              BANCO_DO_CLIENTE_INDISPONIVEL: BANCO_FORA,
            },
          })}
        </Faixa>
      )}
    </Dialogo>
  );
}

function RecusarOperacao({
  operacao,
  aoFechar,
  aoRecusada,
}: {
  operacao: Operacao;
  aoFechar: () => void;
  aoRecusada: (o: Operacao) => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const recusar = useMutation({
    mutationFn: () => api!.recusarOperacaoDeDados({ caminho: { id: operacao.id } }),
    onSuccess: (o) => {
      cache.setQueryData(chaves.operacaoDeDados(o.id), o);
      aoRecusada(o);
    },
    onSettled: () => {
      void cache.invalidateQueries({ queryKey: ['integracoes-de-dados', 'operacoes'] });
      void cache.invalidateQueries({ queryKey: chaves.historicoDaOperacaoDeDados(operacao.id) });
    },
  });
  return (
    <Dialogo
      titulo={`Recusar a v${operacao.versao}?`}
      largura="estreita"
      papel="alertdialog"
      aoFechar={() => {
        if (!recusar.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            {...travar(recusar.isPending ? 'Recusando…' : null)}
          >
            Cancelar
          </Botao>
          <Botao
            variante="perigo"
            onClick={() => recusar.mutate()}
            {...travar(recusar.isPending ? 'Recusando…' : null)}
          >
            {recusar.isPending ? 'Recusando…' : 'Recusar'}
          </Botao>
        </>
      }
    >
      <p className="operacao-sql__nota">
        A versão não vai ao ar. Diga a quem escreveu o que mudar: o serviço não guarda o motivo.
        Salvar de novo cria uma versão nova para revisão.
      </p>
      {recusar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(recusar.error, {
            generica: 'Não foi possível recusar agora. Tente de novo.',
            porCodigo: { ENGINE_INDISPONIVEL: SERVICO_FORA },
          })}
        </Faixa>
      )}
    </Dialogo>
  );
}

function HistoricoDaOperacao({
  operacao,
  meuId,
  aoFechar,
}: {
  operacao: Operacao;
  meuId: string | null;
  aoFechar: () => void;
}): ReactNode {
  const historico = useHistoricoDaOperacaoDeDados(operacao.id);
  return (
    <Dialogo
      titulo={`Histórico de ${operacao.nome}`}
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <Botao variante="fantasma" onClick={aoFechar}>
          Fechar
        </Botao>
      }
    >
      <Estado
        consulta={historico}
        forma="lista"
        linhas={4}
        vazio={{
          quando: (d) => d.itens.length === 0,
          titulo: 'Sem histórico',
          porque: 'O serviço ainda não registrou nada desta operação.',
        }}
      >
        {(dados) => (
          <ol className="operacao-sql__historico">
            {dados.itens.map((h, i) => (
              <li key={`${h.versao}-${h.em}-${i}`} className="operacao-sql__evento">
                <span className="operacao-sql__evento-versao">v{h.versao}</span>
                <span>
                  <strong>{oQueAconteceu(h.acao)}</strong>
                  <span className="operacao-sql__nota">
                    {' '}
                    · {quemFez(h.autor_id, meuId)} ·{' '}
                    {new Date(h.em).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </Estado>
    </Dialogo>
  );
}
